import { useState, useEffect } from 'react';
import { Card } from './ui/Card';
import { Scale, ChevronDown, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import orgPolicyService, { type OrgPolicies, type OvertimeRules } from '../services/orgPolicyService';
import inductionService from '../services/induction';
import type { MyPolicyFacts } from '../types/induction';

interface Props {
  orgId: number;
  userId?: number;
  payFrequency?: string;
}

function formatHour(h: number): string {
  const hour = Math.floor(h);
  const mins = Math.round((h - hour) * 60);
  const period = hour >= 12 ? 'PM' : 'AM';
  const display = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return mins > 0 ? `${display}:${String(mins).padStart(2, '0')} ${period}` : `${display} ${period}`;
}

/**
 * Hours, lunch, leave and sick-leave evidence, worded from the same figures the
 * written hours-and-leave policy is filled from — so the summary and the policy
 * it links to cannot disagree. Readable by every member, unlike the settings.
 */
function buildFactLines(facts: MyPolicyFacts['facts'], p: OrgPolicies | null): string[] {
  const lines: string[] = [];
  if (facts.work_schedule.length) {
    lines.push(`Standard hours: ${facts.work_schedule.join('; ')}${facts.ordinary_hours ? ` — ${facts.ordinary_hours} a week after unpaid lunches` : ''}. Your own roster may differ.`);
  }
  if (facts.lunch_minutes && facts.lunch_threshold) {
    lines.push(`A ${facts.lunch_minutes}-minute unpaid lunch comes off automatically on days longer than ${facts.lunch_threshold}. Only work through lunch when your supervisor asks you to.`);
  }
  if (facts.annual_leave || facts.personal_leave) {
    const parts = [
      facts.annual_leave && `${facts.annual_leave} of annual leave`,
      facts.personal_leave && `${facts.personal_leave} of personal/carer's leave`,
    ].filter(Boolean);
    lines.push(`Full-time employees accrue ${parts.join(' and ')} a year, pro rata for part-time.`);
  }
  if (facts.sick_cert_days) {
    const enforcement = p?.sick_cert_enforcement === 'block_approval'
      ? ' It must be provided before the leave is approved.'
      : p?.sick_cert_enforcement === 'warn_approver'
        ? ' Your manager is told when it may be needed.'
        : '';
    lines.push(`Evidence — a medical certificate, pharmacist's certificate or statutory declaration — may be asked for when you are away more than ${facts.sick_cert_days} in a row.${enforcement}`);
  }
  return lines;
}

/** Overtime and public-holiday rules. Only for readers who can see the settings. */
function buildPolicySummary(p: OrgPolicies, effectiveOT: OvertimeRules, payFrequency?: string): string[] {
  const lines: string[] = [];
  const isFortnightly = payFrequency === 'fortnightly';

  // Overtime time-of-day (from effective rules, not just org defaults)
  const otOutside = effectiveOT.overtime_til_mode
    ? `accrue as Time in Lieu`
    : `are paid at ${effectiveOT.overtime_multiplier}x`;
  lines.push(`Weekday hours between ${formatHour(effectiveOT.overtime_end_hour)} and ${formatHour(effectiveOT.overtime_start_hour)} are paid at your normal rate. Hours outside this window ${otOutside}.`);

  // Saturday
  if (effectiveOT.saturday_all_overtime) {
    const satRate = effectiveOT.saturday_til_mode
      ? `accrue as Time in Lieu`
      : `are paid at ${effectiveOT.saturday_multiplier}x`;
    lines.push(`All Saturday hours ${satRate}.`);
  } else {
    lines.push(`Saturday hours follow the same time-of-day rules as weekdays.`);
  }

  // Sunday
  if (effectiveOT.sunday_all_overtime) {
    const sunRate = effectiveOT.sunday_til_mode
      ? `accrue as Time in Lieu`
      : `are paid at ${effectiveOT.sunday_multiplier}x`;
    lines.push(`All Sunday hours ${sunRate}.`);
  } else {
    lines.push(`Sunday hours follow the same time-of-day rules as weekdays.`);
  }

  // Period thresholds (org-level only — no per-user threshold override exists)
  if (isFortnightly) {
    const action = p.fortnightly_excess_category === 'Time in Lieu'
      ? 'accrue as Time in Lieu'
      : `be paid as overtime`;
    lines.push(`Your fortnightly pay period allows your rostered hours at base rate, with paid leave counted towards them. Hours beyond this ${action}. With no roster set, the limit is ${p.fortnightly_overtime_threshold_hours}h.`);
  } else {
    lines.push(`Your weekly pay period allows up to ${p.weekly_overtime_threshold_hours}h at base rate. Hours beyond this are paid as overtime.`);
  }

  // Public holidays
  if (p.ph_require_adjacent_workday) {
    if (p.ph_noshow_action === 'skip_entry') {
      lines.push(`To receive public holiday pay, you must work the day before or after the holiday.`);
    } else if (p.ph_noshow_action === 'flag_review') {
      lines.push(`Public holiday pay may require review if you did not work the day before or after.`);
    } else {
      lines.push(`Public holiday pay is provided for scheduled public holidays.`);
    }
  } else {
    lines.push(`Public holiday pay is provided for all scheduled public holidays.`);
  }

  if (p.annual_leave_max_balance_hours > 0) {
    lines.push(`Annual leave balance is capped at ${p.annual_leave_max_balance_hours}h.`);
  }
  if (p.personal_leave_max_balance_hours > 0) {
    lines.push(`Personal leave balance is capped at ${p.personal_leave_max_balance_hours}h.`);
  }

  return lines;
}

export default function PolicySummaryCard({ orgId, userId, payFrequency }: Props) {
  const [policies, setPolicies] = useState<OrgPolicies | null>(null);
  const [effectiveOT, setEffectiveOT] = useState<OvertimeRules | null>(null);
  const [policyFacts, setPolicyFacts] = useState<MyPolicyFacts | null>(null);
  const [loading, setLoading] = useState(true);
  const [factsLoading, setFactsLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  // Readable by every member, so employees without an HR seat still get the
  // hours, lunch and leave lines — and the link to the policy they come from.
  useEffect(() => {
    let cancelled = false;
    inductionService.myPolicyFacts()
      .then((f) => { if (!cancelled) setPolicyFacts(f); })
      .catch(() => { if (!cancelled) setPolicyFacts(null); })
      .finally(() => { if (!cancelled) setFactsLoading(false); });
    return () => { cancelled = true; };
  }, [orgId, userId]);

  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;

    const freq = payFrequency === 'fortnightly' || payFrequency === 'weekly' ? payFrequency : null;

    Promise.all([
      orgPolicyService.getPolicies(orgId),
      freq ? orgPolicyService.getOrgOvertimePolicy(orgId, freq) : Promise.resolve(null),
      userId ? orgPolicyService.getUserOvertimeOverride(userId) : Promise.resolve(null),
    ]).then(([policiesRes, freqRes, userRes]) => {
      if (cancelled) return;

      if (!policiesRes.success || !policiesRes.data) return;
      const orgPolicies = policiesRes.data;
      setPolicies(orgPolicies);

      // Resolve effective overtime rules: user override > frequency policy > org default
      const userOverride = userRes?.success ? userRes.data : null;
      const freqPolicy = freqRes?.success ? freqRes.data : null;

      const resolved: OvertimeRules = userOverride ?? freqPolicy ?? {
        overtime_start_hour:   orgPolicies.overtime_start_hour,
        overtime_end_hour:     orgPolicies.overtime_end_hour,
        overtime_multiplier:   orgPolicies.overtime_multiplier,
        overtime_til_mode:     orgPolicies.overtime_til_mode,
        saturday_all_overtime: orgPolicies.saturday_all_overtime,
        saturday_multiplier:   orgPolicies.saturday_multiplier,
        saturday_til_mode:     orgPolicies.saturday_til_mode,
        sunday_all_overtime:   orgPolicies.sunday_all_overtime,
        sunday_multiplier:     orgPolicies.sunday_multiplier,
        sunday_til_mode:       orgPolicies.sunday_til_mode,
      };

      setEffectiveOT(resolved);
    }).catch(() => {
      // This card sits on /timesheet for EVERY employee, and
      // GET /organisations/:id/policies is gated on a timesheet.hr seat — so for
      // anyone without one it rejects with a 403. There was no catch here, so
      // that became an `unhandledrejection`, which the crash reporter dutifully
      // filed as an application error on every load of the page.
      //
      // Swallowing is right rather than lazy: without the settings the card
      // still shows the hours, lunch and leave lines from /inductions/me/
      // policy-facts (open to every member) and only the overtime and
      // public-holiday lines drop out. Widening the settings endpoint is an
      // authorisation decision, not a cleanup, so it is left alone here.
      if (!cancelled) {
        setPolicies(null);
        setEffectiveOT(null);
      }
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });

    return () => { cancelled = true; };
  }, [orgId, userId, payFrequency]);

  if (loading || factsLoading) return null;

  const lines = [
    ...(policyFacts ? buildFactLines(policyFacts.facts, policies) : []),
    ...(policies && effectiveOT ? buildPolicySummary(policies, effectiveOT, payFrequency) : []),
  ];
  const writtenPolicy = policyFacts?.policy ?? null;
  if (lines.length === 0 && !writtenPolicy) return null;

  return (
    <Card className="mt-8">
      <button
        onClick={() => setExpanded(prev => !prev)}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-neutral-50 transition-colors rounded-xl"
      >
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-slate-100 flex items-center justify-center">
            <Scale className="h-5 w-5 text-slate-600" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-neutral-800">Your Company Policies</h3>
            <p className="text-xs text-neutral-500">Overtime, leave, and pay rules that apply to you</p>
          </div>
        </div>
        {expanded
          ? <ChevronDown className="h-5 w-5 text-neutral-400" />
          : <ChevronRight className="h-5 w-5 text-neutral-400" />
        }
      </button>
      {expanded && (
        <div className="px-5 pb-5 pt-0">
          <ul className="space-y-2.5">
            {lines.map((line, i) => (
              <li key={i} className="flex gap-2.5 text-sm text-neutral-700 leading-relaxed">
                <span className="text-slate-400 mt-1 shrink-0">&#8226;</span>
                <span>{line}</span>
              </li>
            ))}
          </ul>
          {writtenPolicy && (
            <p className="mt-4 text-sm">
              <Link to={`/my-inductions/policies/${writtenPolicy.id}`} className="font-medium text-primary-700 hover:underline break-words">
                Read the full policy: {writtenPolicy.title}
              </Link>
              {writtenPolicy.status !== 'acknowledged' && (
                <span className="text-neutral-500"> — please read and acknowledge it.</span>
              )}
            </p>
          )}
        </div>
      )}
    </Card>
  );
}
